import { type Editor as TiptapEditor } from '@tiptap/react';

export class DictationInsertionError extends Error {}

const findProseMirrorEditor = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;
  if (element.classList.contains('ProseMirror')) return element;
  return findProseMirrorEditor(element.parentElement);
};

const isContentEditable = (element: HTMLElement): boolean => {
  return element.isContentEditable || element.contentEditable === 'true';
};

/**
 * Calculates the character offset of a selection point within an element
 * by walking all text nodes in document order.
 */
const getTextOffsetInElement = (
  root: Element,
  container: Node,
  offset: number
): number => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let total = 0;
  let node: Node | null;

  while ((node = walker.nextNode())) {
    if (node === container) return total + offset;
    total += node.textContent?.length ?? 0;
  }
  return total;
};

export interface SelectionState {
  element: HTMLElement;
  inputSelection?: {
    start: number;
    end: number;
  };
  rangeSelection?: {
    startContainer: Node;
    startOffset: number;
    endContainer: Node;
    endOffset: number;
  };
}

export const captureSelectionState = (): SelectionState | null => {
  const activeElement = document.activeElement;

  if (!activeElement || !(activeElement instanceof HTMLElement)) {
    return null;
  }

  if (
    activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement
  ) {
    return {
      element: activeElement,
      inputSelection: {
        start: activeElement.selectionStart ?? activeElement.value.length,
        end: activeElement.selectionEnd ?? activeElement.value.length,
      },
    };
  }

  const proseMirrorElement = findProseMirrorEditor(activeElement);
  if (proseMirrorElement || isContentEditable(activeElement)) {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return {
        element: activeElement,
        rangeSelection: {
          startContainer: range.startContainer,
          startOffset: range.startOffset,
          endContainer: range.endContainer,
          endOffset: range.endOffset,
        },
      };
    }
    return {
      element: activeElement,
    };
  }

  return null;
};

export const restoreSelectionState = (state: SelectionState | null): boolean => {
  if (!state) return false;

  try {
    if (document.activeElement !== state.element) {
      state.element.focus();
    }

    if (
      state.element instanceof HTMLInputElement ||
      state.element instanceof HTMLTextAreaElement
    ) {
      if (state.inputSelection) {
        state.element.setSelectionRange(
          state.inputSelection.start,
          state.inputSelection.end
        );
        return true;
      }
      return false;
    }

    if (state.rangeSelection) {
      const selection = window.getSelection();
      if (!selection) return false;

      try {
        const range = document.createRange();
        range.setStart(
          state.rangeSelection.startContainer,
          state.rangeSelection.startOffset
        );
        range.setEnd(state.rangeSelection.endContainer, state.rangeSelection.endOffset);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
      } catch {
        return false;
      }
    }

    return false;
  } catch {
    return false;
  }
};

export const getInsertionTarget = (): HTMLElement | null => {
  const activeElement = document.activeElement;

  if (!activeElement || !(activeElement instanceof HTMLElement)) {
    return null;
  }

  if (
    activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement
  ) {
    return activeElement;
  }

  const proseMirrorElement = findProseMirrorEditor(activeElement);
  if (proseMirrorElement) {
    return activeElement;
  }

  if (isContentEditable(activeElement)) {
    return activeElement;
  }

  return null;
};

interface GetTextContextOptions {
  editor?: TiptapEditor | null;
  /** Pre-captured cursor position to use instead of reading live editor state */
  capturedCursorPos?: number;
}

/**
 * Extracts the text before and after the cursor position from a selection state.
 * When a TipTap editor is provided, uses doc.textBetween() for accurate
 * text extraction with proper block separators (newlines between paragraphs).
 */
export const getTextContext = (
  state: SelectionState | null,
  options?: GetTextContextOptions
): { precedingText: string; followingText: string } => {
  const { editor, capturedCursorPos } = options ?? {};

  // If we have a TipTap editor with a captured cursor position, use that even if state is null
  // (state can be null if focus shifted away from the editor, e.g., to a button)
  if (editor && capturedCursorPos !== undefined) {
    const docSize = editor.state.doc.content.size;
    const safePos = Math.min(capturedCursorPos, docSize);
    return {
      precedingText: editor.state.doc.textBetween(0, safePos, '\n'),
      followingText: editor.state.doc.textBetween(safePos, docSize, '\n'),
    };
  }

  if (!state) {
    return { precedingText: '', followingText: '' };
  }

  const { element, inputSelection, rangeSelection } = state;

  // Handle input/textarea elements
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    const value = element.value;
    const cursorPos = inputSelection?.start ?? value.length;
    return {
      precedingText: value.slice(0, cursorPos),
      followingText: value.slice(inputSelection?.end ?? cursorPos),
    };
  }

  // Handle ProseMirror/contentEditable elements
  const proseMirrorElement = findProseMirrorEditor(element);
  if (proseMirrorElement || isContentEditable(element)) {
    // If we have a TipTap editor, use doc.textBetween for accurate extraction
    if (editor) {
      const cursorPos = capturedCursorPos ?? editor.state.selection.from;
      const docSize = editor.state.doc.content.size;

      return {
        precedingText: editor.state.doc.textBetween(0, cursorPos, '\n'),
        followingText: editor.state.doc.textBetween(cursorPos, docSize, '\n'),
      };
    }

    // Fallback: use DOM-based extraction (less accurate for block separators)
    const targetElement = proseMirrorElement || element;
    const fullText = targetElement.textContent || '';

    if (rangeSelection) {
      const startOffset = getTextOffsetInElement(
        targetElement,
        rangeSelection.startContainer,
        rangeSelection.startOffset
      );
      const endOffset = getTextOffsetInElement(
        targetElement,
        rangeSelection.endContainer,
        rangeSelection.endOffset
      );

      return {
        precedingText: fullText.slice(0, startOffset),
        followingText: fullText.slice(endOffset),
      };
    }

    // No range selection, return full text as preceding (cursor at end)
    return { precedingText: fullText, followingText: '' };
  }

  return { precedingText: '', followingText: '' };
};

export const insertText = (text: string, selectionState?: SelectionState | null) => {
  const state = selectionState || captureSelectionState();

  if (!state) {
    throw new DictationInsertionError();
  }

  let restored = restoreSelectionState(state);

  if (!restored) {
    try {
      if (state.element !== document.activeElement) {
        state.element.focus();
      }
      restored = restoreSelectionState(state);
    } catch {
      // Continue anyway, will use current selection
    }
  }

  const target = state.element;

  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    const start = state.inputSelection?.start ?? target.value.length;
    const end = state.inputSelection?.end ?? target.value.length;

    // Restore selection so execCommand inserts at the correct position
    target.setSelectionRange(start, end);

    // Try execCommand first -- it integrates with the browser's undo stack
    // and fires a native input event that React picks up as onChange.
    // This is the same approach used for contentEditable elements below.
    if (document.execCommand('insertText', false, text)) {
      return;
    }

    // Fallback: native setter + synthetic InputEvent (no undo/redo support)
    const newValue = target.value.slice(0, start) + text + target.value.slice(end);

    const descriptor = Object.getOwnPropertyDescriptor(
      target instanceof HTMLInputElement
        ? window.HTMLInputElement.prototype
        : window.HTMLTextAreaElement.prototype,
      'value'
    );

    if (descriptor?.set) {
      descriptor.set.call(target, newValue);
    } else {
      target.value = newValue;
    }

    target.setSelectionRange(start + text.length, start + text.length);

    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
      data: text,
    });
    target.dispatchEvent(inputEvent);
    return;
  }

  const proseMirrorElement = findProseMirrorEditor(target);
  if (proseMirrorElement || isContentEditable(target)) {
    if (document.execCommand && document.execCommand('insertText', false, text)) {
      return;
    }

    if (proseMirrorElement) {
      let selection = window.getSelection();

      if (!selection || selection.rangeCount === 0) {
        if (state.rangeSelection && selection) {
          try {
            const range = document.createRange();
            range.setStart(
              state.rangeSelection.startContainer,
              state.rangeSelection.startOffset
            );
            range.setEnd(
              state.rangeSelection.endContainer,
              state.rangeSelection.endOffset
            );
            selection.removeAllRanges();
            selection.addRange(range);
          } catch {
            throw new DictationInsertionError();
          }
        } else {
          throw new DictationInsertionError();
        }
      }

      selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) throw new DictationInsertionError();

      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    if (isContentEditable(target) && !proseMirrorElement) {
      throw new DictationInsertionError();
    }
  }

  throw new DictationInsertionError();
};
